import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, MailCheck, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Field, TextInput, ErrorBox, btnPrimary } from "@/components/common/Form";
import { toast } from "sonner";

function AuthShell({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-sm" data-testid={testId}>
        <div className="h-12 w-12 rounded-xl bg-accent text-secondary grid place-items-center mb-4"><Icon size={22} /></div>
        <h1 className="text-3xl font-serif font-bold text-stone-900">{title}</h1>
        <p className="mt-1 text-sm text-stone-500">{subtitle}</p>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setDone(true); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell icon={KeyRound} title="Forgot password" subtitle="We'll email you a secure link to reset your password." testId="forgot-page">
      {done ? (
        <div data-testid="forgot-success" className="rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm p-4">
          If an account exists for <b>{email}</b>, a reset link has been sent. Check your inbox (and spam folder).
          <Link to="/login" className="block mt-3 font-semibold text-secondary">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorBox message={error} testId="forgot-error" />
          <Field label="Email"><TextInput testId="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <button disabled={loading} data-testid="forgot-submit" className={`${btnPrimary} w-full`}>{loading ? "Sending..." : "Send reset link"}</button>
          <p className="text-center text-sm text-stone-500"><Link to="/login" className="font-semibold text-secondary">Back to sign in</Link></p>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (password !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell icon={ShieldCheck} title="Set a new password" subtitle="Use 8+ characters with uppercase, lowercase and a number." testId="reset-page">
      {!token ? <ErrorBox message="This reset link is invalid. Please request a new one." testId="reset-error" /> : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorBox message={error} testId="reset-error" />
          <Field label="New password"><TextInput testId="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
          <Field label="Confirm password"><TextInput testId="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
          <button disabled={loading} data-testid="reset-submit" className={`${btnPrimary} w-full`}>{loading ? "Saving..." : "Reset password"}</button>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const { setUser } = useStore();
  const [state, setState] = useState("loading");
  const [message, setMessage] = useState("");
  useEffect(() => {
    let cancelled = false;
    const token = params.get("token");
    if (!token) { setState("error"); setMessage("This verification link is invalid."); return; }
    api.post("/auth/verify-email", { token })
      .then(() => { if (cancelled) return; setState("ok"); api.get("/auth/me").then((r) => !cancelled && setUser(r.data.user)).catch(() => {}); })
      .catch((err) => { if (!cancelled) { setState("error"); setMessage(formatApiError(err.response?.data?.detail)); } });
    return () => { cancelled = true; };
  }, [params, setUser]);
  return (
    <AuthShell icon={MailCheck} title="Email verification" subtitle="Securing your Shri Vrindavan Shop account." testId="verify-email-page">
      {state === "loading" && <p className="text-sm text-stone-500" data-testid="verify-loading">Verifying...</p>}
      {state === "ok" && <div data-testid="verify-success" className="rounded-lg bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm p-4">Your email is verified. <Link to="/account" className="font-semibold text-secondary">Go to my account</Link></div>}
      {state === "error" && <ErrorBox message={message} testId="verify-error" />}
    </AuthShell>
  );
}
